import * as React from 'react';
import MenuIcon from '@mui/icons-material/Menu';
import {
  AppBar,
  Toolbar,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Avatar,
  Container,
  Button,
  Tooltip,
  Switch,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import useAuth from '../hooks/useAuth';
import MenuAction from '../models/components/MenuAction';
import { signOut } from '../firebaseSetup';
import { useNavigate } from 'react-router-dom';
import StarPurple500Icon from '@mui/icons-material/StarPurple500';
import { FEATURE_KEYS } from '../models/FeatureFlags';
import {
  clearLocalFeatureFlagOverrides,
  getCachedFeatureFlags,
  getLocalFeatureFlagOverride,
  isLocalFeatureFlagDebugHost,
  loadFeatureFlags,
  setLocalFeatureFlagOverride,
} from '../services/FeatureFlagService';
import {
  clearReleaseHighlightAcknowledgement,
  resolveReleaseHighlights,
  saveReleaseHighlightAcknowledgement,
} from '../services/ReleaseHighlightService';
import { GoogleSheetsService } from '../services/GoogleSheetsService';
import { Release, ReleaseNoteRow } from '../models/ReleaseHighlights';

const featureFlagKeys = Object.values(FEATURE_KEYS);

const formatFeatureFlagLabel = (key: string) =>
  key
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const readLocalFeatureFlagOverrides = () =>
  Object.fromEntries(featureFlagKeys.map((key) => [key, getLocalFeatureFlagOverride(key)]));

const areReleaseListsEqual = (left: Release[], right: Release[]) =>
  left.length === right.length && left.every((release, index) => release.version === right[index]?.version);

function ResponsiveAppBar() {
  const [anchorElNav, setAnchorElNav] = React.useState<null | HTMLElement>(
    null,
  );
  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(
    null,
  );
  const [anchorElFeatureFlags, setAnchorElFeatureFlags] = React.useState<null | HTMLElement>(null);
  const [riderResultsEnabled, setRiderResultsEnabled] = React.useState(
    () => getCachedFeatureFlags()?.isEnabled(FEATURE_KEYS.riderResults) ?? false,
  );
  const [releaseHighlightRelease, setReleaseHighlightRelease] = React.useState<Release | null>(null);
  const [releaseHighlightReleases, setReleaseHighlightReleases] = React.useState<Release[]>([]);
  const [whatsNewOpen, setWhatsNewOpen] = React.useState(false);
  const [whatsNewAcknowledged, setWhatsNewAcknowledged] = React.useState(false);
  const [localFeatureFlagOverrides, setLocalFeatureFlagOverrides] = React.useState<Record<string, boolean | null>>(() =>
    isLocalFeatureFlagDebugHost() ? readLocalFeatureFlagOverrides() : {},
  );

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };
  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };
  const handleOpenFeatureFlagMenu = (event?: React.MouseEvent<HTMLElement>) => {
    if (event?.currentTarget) {
      setAnchorElFeatureFlags(event.currentTarget);
    }
    handleCloseNavMenu();
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleCloseFeatureFlagMenu = () => {
    setAnchorElFeatureFlags(null);
  };

  const user = useAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    let mounted = true;

    const loadFlags = async () => {
      const flags = await loadFeatureFlags();
      const nextEnabled = flags.isEnabled(FEATURE_KEYS.riderResults);
      if (mounted && riderResultsEnabled !== nextEnabled) {
        setRiderResultsEnabled(nextEnabled);
      }
    };

    loadFlags();

    return () => {
      mounted = false;
    };
  }, [riderResultsEnabled]);

  const settings = [new MenuAction('Logout', () => signOut())];
  const localDebugEnabled = isLocalFeatureFlagDebugHost();

  const toggleLocalFeatureFlagOverride = (key: string) => {
    const nextEnabled = localFeatureFlagOverrides[key] !== true;
    setLocalFeatureFlagOverride(key, nextEnabled);
    if (key === FEATURE_KEYS.releaseHighlights && nextEnabled) {
      clearReleaseHighlightAcknowledgement();
      setWhatsNewAcknowledged(false);
    }
    setLocalFeatureFlagOverrides(readLocalFeatureFlagOverrides());
    handleCloseFeatureFlagMenu();
  };

  const clearLocalFeatureFlagDebugOverrides = () => {
    clearLocalFeatureFlagOverrides();
    setLocalFeatureFlagOverrides(readLocalFeatureFlagOverrides());
    handleCloseFeatureFlagMenu();
  };

  const showLatestReleaseHighlights = () => {
    if (!releaseHighlightRelease) return;
    saveReleaseHighlightAcknowledgement(releaseHighlightRelease.version);
    setWhatsNewAcknowledged(true);
    setWhatsNewOpen(true);
  };

  React.useEffect(() => {
    let mounted = true;

    const loadReleaseHighlights = async () => {
      const flags = await loadFeatureFlags();
      let releaseNoteRows: ReleaseNoteRow[] = [];
      try {
        releaseNoteRows = await new GoogleSheetsService().getReleaseNoteRows();
      } catch {
        releaseNoteRows = [];
      }
      const result = resolveReleaseHighlights({
        flagSet: flags,
        releaseNoteRows,
      });

      if (!mounted) return;
      setReleaseHighlightRelease((currentRelease) =>
        currentRelease?.version === result.release?.version ? currentRelease : result.release,
      );
      setReleaseHighlightReleases((currentReleases) =>
        areReleaseListsEqual(currentReleases, result.releases) ? currentReleases : result.releases,
      );
      if (!result.release) {
        setWhatsNewAcknowledged((currentAcknowledged) => (currentAcknowledged ? false : currentAcknowledged));
        setWhatsNewOpen((currentOpen) => (currentOpen ? false : currentOpen));
      }
    };

    loadReleaseHighlights();

    return () => {
      mounted = false;
    };
  }, [localFeatureFlagOverrides[FEATURE_KEYS.releaseHighlights]]);

  const pages = [
    new MenuAction('Schedule', () => navigateTo('/schedule')),
    new MenuAction('Show Results', () => navigateTo('/showResults')),
    ...(riderResultsEnabled
      ? [
          new MenuAction('Rider Results', () => navigateTo('/riderResults')),
        ]
      : []),
    new MenuAction('Standings', () => navigateTo('/standings')),
    new MenuAction('Sponsors', () => navigateTo('/sponsors')),
    new MenuAction('Arenas', () => navigateTo('/arenas')),
    new MenuAction('About', () => navigateTo('/about')),
  ];
  if (user && user.isAdmin) {
    pages.push(
      new MenuAction('Admin', () => navigateTo('/admin/dashboard')),
    );
  }
  if (localDebugEnabled) {
    pages.push(
      new MenuAction('Feature Flags', handleOpenFeatureFlagMenu),
    );
  }

  const navigateTo = (path: string) => {
    navigate(path);
    handleCloseNavMenu();
  };
  const whatsNewDialogTitle =
    releaseHighlightReleases.length > 1
      ? `What's new through ${releaseHighlightRelease?.version}`
      : `What's new in ${releaseHighlightRelease?.version}`;

  return (
    <AppBar position="static">
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          <Box
            component="img"
            sx={{
              height: 48,
                width: 126,
              display: { xs: 'none', md: 'flex' },
              mr: 1,
            }}
            src="https://i.imgur.com/0uvYwkM.png"
            alt="logo"
          />
          <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleOpenNavMenu}
              color="inherit"
            >
              <MenuIcon />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorElNav}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
              sx={{
                display: { xs: 'block', md: 'none' },
              }}
            >
              {pages.map((page) => (
                <MenuItem key={page.name} onClick={page.action}>
                  <Typography textAlign="center">{page.name}</Typography>
                  {page.isPremium && (
                    <StarPurple500Icon className="premium-star" />
                  )}
                </MenuItem>
              ))}
            </Menu>
          </Box>

          <Container
            sx={{
              display: { xs: 'flex', md: 'none' },
              justifyContent: 'center',
              mr: 1,
            }}
          >
            <Box
              component="img"
              sx={{
                height: 48,
                width: 126,
              }}
              src="https://i.imgur.com/0uvYwkM.png"
              alt="logo"
            />
          </Container>
          <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
            {pages.map((page) => (
              <MenuItem key={page.name} onClick={page.action}>
                <Typography textAlign="center">{page.name}</Typography>
                {page.isPremium && (
                  <StarPurple500Icon className="premium-star" />
                )}
              </MenuItem>
            ))}
          </Box>

          {localDebugEnabled && (
            <Menu
              id="feature-flags-menu"
              anchorEl={anchorElFeatureFlags}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              open={Boolean(anchorElFeatureFlags)}
              onClose={handleCloseFeatureFlagMenu}
            >
              {featureFlagKeys.map((key) => (
                <MenuItem key={key} onClick={() => toggleLocalFeatureFlagOverride(key)}>
                  <Typography sx={{ minWidth: 160 }}>{formatFeatureFlagLabel(key)}</Typography>
                  <Switch
                    edge="end"
                    checked={localFeatureFlagOverrides[key] === true}
                    inputProps={{ 'aria-label': `${formatFeatureFlagLabel(key)} local override` }}
                  />
                </MenuItem>
              ))}
              <MenuItem onClick={clearLocalFeatureFlagDebugOverrides}>
                <Typography sx={{ minWidth: 160 }}>Use Google Sheets Flags</Typography>
              </MenuItem>
            </Menu>
          )}

          <Box sx={{ flexGrow: 0 }}>
            {user && user.isAdmin && (
              <>
                <Tooltip title="Open settings">
                  <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                    {user.user?.displayName && user.user?.photoURL ? (
                      <Avatar
                        alt={user.user.displayName}
                        src={user.user.photoURL}
                      />
                    ) : (
                      <Avatar />
                    )}
                  </IconButton>
                </Tooltip>
                <Menu
                  sx={{ mt: '45px' }}
                  id="menu-appbar"
                  anchorEl={anchorElUser}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  open={Boolean(anchorElUser)}
                  onClose={handleCloseUserMenu}
                >
                  {settings.map((setting) => (
                    <MenuItem key={setting.name} onClick={setting.action}>
                      <Typography textAlign="center">{setting.name}</Typography>
                    </MenuItem>
                  ))}
                </Menu>
              </>
            )}
            {(!user || !user.isAdmin) && (
              <div className="user-placeholder"></div>
            )}
          </Box>
          {releaseHighlightRelease && !whatsNewAcknowledged && (
            <Fab
              color="secondary"
              variant="extended"
              size="small"
              onClick={showLatestReleaseHighlights}
              aria-label={`What's new in release ${releaseHighlightRelease.version}`}
              sx={{ position: 'fixed', right: 16, bottom: 16, zIndex: (theme) => theme.zIndex.tooltip }}
            >
              What's new
            </Fab>
          )}
          <Dialog
            open={whatsNewOpen}
            onClose={() => setWhatsNewOpen(false)}
            aria-labelledby="whats-new-dialog-title"
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle id="whats-new-dialog-title">
              {whatsNewDialogTitle}
            </DialogTitle>
            <DialogContent dividers>
              {releaseHighlightReleases.map((release) => (
                <Box key={release.version} sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {release.version}: {release.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {release.summary}
                  </Typography>
                </Box>
              ))}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setWhatsNewOpen(false)}>Close</Button>
            </DialogActions>
          </Dialog>
        </Toolbar>
      </Container>
    </AppBar>
  );
}
export default ResponsiveAppBar;
